import { queryOptions, useQuery } from '@tanstack/react-query';
import { getV1MessagingChannelsUnreadCounts } from '@treadinc/horizon-api-spec';

import { MessagingUnreadCounts } from '~hooks/useMessaging/models';
import { MESSAGING_UNREAD_COUNTS_QUERY_KEY } from '~queries/messagingQueryKeys';

const NO_UNREAD_COUNTS = MessagingUnreadCounts.empty();

// Realtime events invalidate the counts, so a badge mounting later can read the cached ones.
const UNREAD_COUNTS_STALE_TIME_MS = 30_000;

export const messagingUnreadCountsQueryOptions = (isEnabled: boolean) =>
  queryOptions({
    queryKey: MESSAGING_UNREAD_COUNTS_QUERY_KEY,
    enabled: isEnabled,
    queryFn: async ({ signal }) => {
      const response = await getV1MessagingChannelsUnreadCounts({ signal });

      return MessagingUnreadCounts.parse(response.data.data);
    },
    retry: false,
    staleTime: UNREAD_COUNTS_STALE_TIME_MS,
  });

// The counts stay cached for every caller, so a disabled one must not read another's fetch.
export const unreadCountsVisibleTo =
  (isEnabled: boolean) => (unreadCounts: MessagingUnreadCounts) =>
    isEnabled ? unreadCounts : NO_UNREAD_COUNTS;

/** A background read, so no toast: the response interceptor already reports a failure to Sentry. */
export default function useMessagingUnreadCountsQuery({
  isEnabled = true,
}: { isEnabled?: boolean } = {}) {
  const { data: unreadCounts = NO_UNREAD_COUNTS } = useQuery({
    ...messagingUnreadCountsQueryOptions(isEnabled),
    select: unreadCountsVisibleTo(isEnabled),
  });

  return { unreadCounts };
}
